/**
 * Auto-recover from stale lazy-loaded chunks after a deploy.
 *
 * When the user has an old index.html open and we ship a new build, the hashed
 * chunk filenames change. Their next lazy import throws:
 *   - "Failed to fetch dynamically imported module"
 *   - "Importing a module script failed"
 *   - "error loading dynamically imported module"
 *
 * We catch those globally and do a one-shot reload. A sessionStorage guard
 * prevents reload loops if the failure is something else entirely.
 */

const RELOAD_KEY = "__lov_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk .* failed/i,
];

function isChunkError(value: unknown): boolean {
  if (!value) return false;
  const message =
    value instanceof Error
      ? value.message
      : typeof value === "string"
        ? value
        : typeof (value as { message?: unknown }).message === "string"
          ? ((value as { message: string }).message)
          : "";
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function shouldReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

export function installChunkReload() {
  if (typeof window === "undefined") return;

  const handle = (err: unknown) => {
    if (!isChunkError(err)) return;
    if (!shouldReload()) return;
    // eslint-disable-next-line no-console
    console.warn("[chunkReload] stale chunk detected, reloading", err);
    window.location.reload();
  };

  window.addEventListener("error", (event) => {
    handle(event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    handle(event.reason);
  });
}
